import { useState, useMemo, useEffect } from 'react';
import { FormControl, FormLabel, Box, Stack } from '@chakra-ui/react';
import { Field } from 'formik';
import { useCompanyForm } from '../../hooks/useCompanyForm';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';
import { FORM_FIELD_PATHS } from '../../constants';

const DefaultPlan = ({ darkMode, errors, defaultPlansData, isAddMode }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { formData, updateFormData, mode } = useCompanyForm();
    const isWholesaler = mode === 'Wholesaler';

    const filteredPlans = useMemo(() => {
        if (
            !formData?.plans ||
            !Array.isArray(formData.plans) ||
            formData.plans.length === 0
        ) {
            return [];
        }
        return formData.plans;
    }, [formData?.plans]);

    // Clear default plan if it's not in the selected plans list
    useEffect(() => {
        if (isWholesaler) return;
        if (formData?.defaultPlanID && filteredPlans.length > 0) {
            const isDefaultPlanInSelected = filteredPlans.some(
                (plan) => plan.id === formData.defaultPlanID,
            );
            if (!isDefaultPlanInSelected) {
                updateFormData(FORM_FIELD_PATHS.DEFAULT_PLAN_ID, '');
                updateFormData(FORM_FIELD_PATHS.DEFAULT_PLAN, '');
            }
        }
    }, [isWholesaler, filteredPlans, formData?.defaultPlanID, updateFormData]);

    const handleSelectChange = (item) => {
        const selectedId = item?.id;
        updateFormData(FORM_FIELD_PATHS.DEFAULT_PLAN_ID, selectedId);

        if (item) {
            updateFormData(FORM_FIELD_PATHS.DEFAULT_PLAN, item.name);
        }
    };

    const hasError = !!errors?.defaultPlanID;

    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            isInvalid={hasError}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <FormLabel sx={formLabelStyling(darkMode)}>
                    Default Plan <span style={{ color: '#e53e3e' }}>*</span>
                </FormLabel>
                <Box flex={1} maxWidth="400px">
                    <Field
                        component={DropDown}
                        id={FORM_FIELD_PATHS.DEFAULT_PLAN_ID}
                        name={FORM_FIELD_PATHS.DEFAULT_PLAN_ID}
                        data={
                            isWholesaler || isAddMode
                                ? defaultPlansData?.defaultPlans
                                : filteredPlans
                        }
                        isLoading={defaultPlansData?.isDefaultPlansLoading}
                        shownValue="name"
                        currentId={formData?.defaultPlanID}
                        selectedId="id"
                        placeholder={
                            isWholesaler
                                ? 'Select default wholesaler plan'
                                : !isAddMode && filteredPlans.length === 0
                                  ? 'Select plans first'
                                  : 'Select default company plan'
                        }
                        handleSelectChange={handleSelectChange}
                        width="100%"
                        darkMode={darkMode}
                        disabled={
                            isWholesaler ? false : filteredPlans.length === 0
                        }
                        validate={() => {
                            if (!formData?.[FORM_FIELD_PATHS.DEFAULT_PLAN_ID]) {
                                return 'Default plan is required';
                            }
                        }}
                    />
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label={
                            isWholesaler
                                ? 'Select the default billing plan for this wholesaler'
                                : 'Select the default billing plan from the plans selected above'
                        }
                        ariaLabel="default-plan-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default DefaultPlan;
